/**
 * Project settings (Godot project.godot analog) — main scene, physics, display.
 */

import type { InputAction } from './InputMap';

export interface ProjectSettingsData {
  application: {
    name: string;
    main_scene: string;
    run_main_scene_in_game: boolean;
  };
  display: {
    width: number;
    height: number;
  };
  physics: {
    common_physics_ticks_per_second: number;
  };
  input: Record<string, InputAction>;
  /** Optional notes / version */
  config_version: number;
}

const KEY = 'fe-project-settings-v1';

export const DEFAULT_PROJECT: ProjectSettingsData = {
  application: {
    name: 'Element Fencing',
    main_scene: 'res://levels/main_arena.tscn',
    run_main_scene_in_game: true,
  },
  display: {
    width: 1280,
    height: 720,
  },
  physics: {
    common_physics_ticks_per_second: 60,
  },
  input: {},
  config_version: 1,
};

export function loadProjectSettings(): ProjectSettingsData {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return structuredClone(DEFAULT_PROJECT);
    const parsed = JSON.parse(raw) as ProjectSettingsData;
    return {
      ...structuredClone(DEFAULT_PROJECT),
      ...parsed,
      application: { ...DEFAULT_PROJECT.application, ...parsed.application },
      display: { ...DEFAULT_PROJECT.display, ...parsed.display },
      physics: { ...DEFAULT_PROJECT.physics, ...parsed.physics },
      input: parsed.input || {},
    };
  } catch {
    return structuredClone(DEFAULT_PROJECT);
  }
}

export function saveProjectSettings(data: ProjectSettingsData): void {
  localStorage.setItem(KEY, JSON.stringify(data));
}

export function updateProjectSettings(partial: Partial<ProjectSettingsData>): ProjectSettingsData {
  const cur = loadProjectSettings();
  const next: ProjectSettingsData = {
    ...cur,
    ...partial,
    application: { ...cur.application, ...partial.application },
    display: { ...cur.display, ...partial.display },
    physics: { ...cur.physics, ...partial.physics },
    input: partial.input ? { ...cur.input, ...partial.input } : cur.input,
  };
  saveProjectSettings(next);
  return next;
}

export function getMainScenePath(): string {
  return loadProjectSettings().application.main_scene || DEFAULT_PROJECT.application.main_scene;
}

export function setMainScenePath(path: string): ProjectSettingsData {
  return updateProjectSettings({ application: { ...loadProjectSettings().application, main_scene: path } });
}
